import React, { useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { setNewPasswordFromLink } from '../../api/authApi';

interface SetNewPasswordFormProps {
  resetKey: string;
  login: string;
}

export const SetNewPasswordForm: React.FC<SetNewPasswordFormProps> = ({ resetKey, login }) => {
  const { t } = useI18n();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    
    if (password.length < 8) {
      setError(t('err_password_short') || 'Hasło musi mieć co najmniej 8 znaków.');
      return;
    }
    
    if (password !== confirmPassword) {
      setError(t('err_passwords_mismatch') || 'Hasła nie są identyczne.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await setNewPasswordFromLink(resetKey, login, password);
      setSuccess(true);
      // Wait a moment then reload to log in
      setTimeout(() => {
        window.location.href = window.cabinetVars?.cabinetUrl || '/moje-konto/';
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || t('err_set_password') || 'Błąd podczas ustawiania hasła. Link mógł wygasnąć.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStyles = () => (
    <style>{`

    
      .cab-reset-form * {
        box-sizing: border-box;
      }
      .cab-reset-form.cab-auth-form {
        background: #F8F6FC;
        border-radius: 32px;
        padding: 48px 32px;
        width: 100%;
        max-width: 644px;
        margin: 0 auto;
        text-align: center;
        font-family: inherit;
        margin-top: 50px;
      }
      .cab-auth-title {
        font-weight: 700;
        font-size: 32px;
        line-height: 1.2;
        text-align: center;
        color: var(--cab-violet);
        margin: 0 0 12px 0;
      }
      .cab-auth-sub {
        font-weight: 400;
        font-size: 16px;
        line-height: 1.5;
        text-align: center;
        color: #6B7280;
        margin: 0 0 32px 0!important;
      }
      .cab-auth-field {
        margin-bottom: 24px;
        text-align: left;
      }
      .cab-auth-input {
        width: 100%;
        height: 56px;
        background: #FFFFFF;
        border: 1px solid #D1D5DB;
        padding: 4px 24px;
        font-weight: 400;
        font-size: 16px;
        color: #111827;
        transition: border-color 0.2s;
        box-sizing: border-box;
        align-items: center;
        border-radius: 100px;
        box-shadow: 0 4px 16px rgba(45, 21, 97, 0.03);
        border: 1px solid transparent;
        transition: border-color 0.2s;
      }
      .cab-auth-input:focus {
        outline: none;
        border-color: var(--cab-violet);
      }
      .cab-auth-btn {
        width: 100%;
        height: 56px;
        background: var(--cab-violet);
        border-radius: 100px;
        border: none;
        color: #FFFFFF;
        font-weight: 700;
        font-size: 16px;
        cursor: pointer;
        transition: background 0.2s;
        margin-bottom: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .cab-auth-btn:hover {
        background: #2a1158;
      }
      .cab-auth-btn:disabled {
        opacity: 0.7;
        cursor: not-allowed;
      }
      .cab-error-msg {
        color: #DC2626;
        font-size: 14px;
        margin-bottom: 16px;
      }
      .cab-success-msg {
        color: #059669;
        font-size: 16px;
        margin-bottom: 16px;
        font-weight: 600;
      }
    `}</style>
  );

  const EyeIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  const EyeOffIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <div className="cab-reset-form cab-auth-form">
      {renderStyles()}

      <h2 className="cab-auth-title">{t('set_new_password_title')}</h2>
      <p className="cab-auth-sub">{t('set_new_password_sub')}</p>

      {success ? (
        <div className="cab-success-msg">
          {t('password_set_success')}
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {error && <div className="cab-error-msg">{error}</div>}

          <div className="cab-auth-field" style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              className="cab-auth-input"
              placeholder={t('new_password_placeholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              style={{ paddingRight: '48px' }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '16px',
                top: '20px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6B7280'
              }}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px', marginLeft: '12px' }}>
              {t('err_password_short')}
            </div>
          </div>

          <div className="cab-auth-field" style={{ position: 'relative' }}>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              className="cab-auth-input"
              placeholder={t('confirm_password_placeholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              style={{ paddingRight: '48px' }}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6B7280'
              }}
            >
              {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>

          <button type="submit" className="cab-auth-btn" disabled={isLoading}>
            {isLoading ? '...' : t('set_password_btn')}
          </button>
        </form>
      )}
    </div>
  );
};
